import { AgGridReact } from "ag-grid-react";
import { Button, Dropdown, Menu, Modal, notification } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import CiqGridViewWrapper from "components/ciq-grid-view-wrapper";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import { newEntityNotificationMessage } from "entity-app/shared-components/new-entity-notification-message";
import { useParams } from "react-router";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { AppContext } from "context/AppProvider";
import { applyBidPackagesLogFilters } from "utils/ag-grid-programmatic-filtering";
import { resetGridFilters } from "entity-app/utils/utils";
import { FeatureTypes } from "entity-app/constants";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";

import {
  EFeaturePageId,
  ErrorMessages,
  ProjectPermissionEnum,
  SidepanelStyles
} from "constants/index";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import ColumnConfigViews from "components/column-config-views";
import { DownOutlined } from "@ant-design/icons";
import ErrorBoundary from "components/error-boundary";
import FeatureBulkEditDurations from "components/feature-bulk-edit-durations";
import { getRowId } from "pages/submittals-log-page/utils";
import { updateGridSelectionOnFilter } from "utils/utils";
import { InfoFeatureType } from "change-events/change-event-polling";
import { CiqIconBox } from "components/svg-icons";
import DemoTextIcon from "components/svg-icons/demo-icon";
import { useFeatureLogV2InlineEdit } from "entity-app/hooks/feature-log-v2-inline-edit";
import BulkEditDeadlineDateModal from "components/bulk-edit-dateline-date/bulk-edit-dateline-date";
import { usePreconstrutionData } from "./hooks/preconstruction-data";
import CreateBidPackageComponent from "./components/create-bid-package";
import BulkEditBidPackageDetails from "./components/bulk-edit-details";
import { usePreconstrutionAgGrid } from "./hooks/preconstruction-ag-grid-data";

const featureKey = "bid-packages"; // URL

function PreconstructionLogPage() {
  const gridRef = useRef<AgGridReact<any>>(null);
  const { projectDetails, tokenContents } = useContext(ProjectContext);

  const cannotWriteAccessOfBidPackage = isPermissionNotGrantted(
    ProjectPermissionEnum.BidPackageWrite,
    tokenContents?.role!
  );

  const pageData = usePreconstrutionData({ gridRef });
  const agGridData = usePreconstrutionAgGrid({
    featureKey,
    milestonesColumns: pageData.milestonesColumns,
    cannotWriteAccessOfBidPackage,
    bidPackageHeader: pageData.bidPackageHeaderMap
  });

  const { onCellEditRequest } = useFeatureLogV2InlineEdit({
    refetchListData: pageData.refetchBidPackageList,
    infoFeatureType: InfoFeatureType.bidPackage
  });
  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const { projectId } = useParams() as any;
  const { bidPackageListFilter, setBidPackageListFilter } =
    useContext(AppContext);

  const [isFirstDataRendered, setIsFirstDataRendered] = useState(false);
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const [selectedRows, setSelectedRows] = useState<Array<any> | null>(null);

  const [bulkEditMode, setBulkEditMode] = useState<
    "details" | "durations" | "deadline_date" | null
  >(null);

  const openNotification = (id: string, title: string) => {
    api.open(
      newEntityNotificationMessage({
        projectId,
        id,
        title,
        featureKey
      })
    );
  };

  const onFirstDataRendered = useCallback(() => {
    if (bidPackageListFilter) {
      applyBidPackagesLogFilters({ gridRef, bidPackageListFilter });
    }
    if (
      pageData.savedColumns?.bid_package_column_config &&
      bidPackageListFilter === null &&
      gridRef.current
    ) {
      UtilColumnConfig.setFilterStateFromData({
        columnStates: pageData.savedColumns?.bid_package_column_config,
        gridRef: {
          api: gridRef.current!.api
        }
      });
    }
    pageData.calculateMaxNumberOfMilestone();

    setTimeout(() => {
      // NOTE: To load initial data once in onFilterApplied method
      setIsFirstDataRendered(true);
    }, 500);
  }, [bidPackageListFilter, pageData]);

  const bulkEditActionMenuItems = useMemo(() => {
    return (
      <Menu
        items={[
          {
            type: "group",
            label: "Bulk Edit",
            key: "bulk-edit-group",
            children: [
              {
                key: 0,
                label: "Details",
                onClick: () => setBulkEditMode("details")
              },
              {
                key: 1,
                label:
                  pageData?.bidPackageHeaderMap?.manual_entry_date ||
                  "Deadline Date",
                onClick: () => setBulkEditMode("deadline_date")
              },
              {
                key: 2,
                label: "Durations",
                onClick: () => setBulkEditMode("durations")
              }
            ]
          }
        ]}
      />
    );
  }, [pageData?.bidPackageHeaderMap?.manual_entry_date]);

  const bulkEditButton = useMemo(() => {
    if (!selectedRows) return null;
    const selectedCount = selectedRows.length;
    if (selectedCount <= 1) return null;
    return (
      <div className="bg-ciq-gray-500 bg-opacity-70 py-1 px-4 flex items-center space-x-2 rounded-md">
        <div className="py-1 whitespace-nowrap">
          {selectedRows?.length} Selected
        </div>
        {selectedCount > 1 && (
          <Dropdown
            overlay={bulkEditActionMenuItems}
            key="PreconstructionLogPage_bulkEdit"
            data-testid="index-dropdown-edit"
          >
            <Button>
              Edit ({selectedCount}) <DownOutlined />
            </Button>
          </Dropdown>
        )}
      </div>
    );
  }, [bulkEditActionMenuItems, selectedRows]);

  const onAllBidPackagesClick = () => {
    resetGridFilters({
      setListFilter: setBidPackageListFilter,
      api: gridRef.current?.api
    });
  };

  const headerItems = () => {
    const menus = [] as JSX.Element[];
    if (!bidPackageListFilter)
      menus.push(
        <FilterChipComponent
          key="PreconstructionLogPage_FilterChip"
          gridRef={gridRef}
          ref={filterChipRef}
        />
      );
    if (bulkEditButton) menus.push(bulkEditButton);
    menus.push(
      <CiqAgSearch key="PreconstructionLogPage_search" gridRef={gridRef} />
    );

    menus.push(
      <ColumnConfigViews
        key="PreconstructionLogPage_ColumnConfigViews"
        featureId={EFeaturePageId.BID_PACKAGE}
        setFilterStateFromData={(columnStates) => {
          if (gridRef.current)
            UtilColumnConfig.setFilterStateFromData({
              columnStates,
              gridRef: {
                api: gridRef.current!.api
              }
            });
        }}
        getColumnStateFromGrid={() => {
          if (gridRef.current) {
            return UtilColumnConfig.getColumnStateFromGrid({
              gridRef: {
                api: gridRef.current!.api
              }
            });
          }
          return null;
        }}
        afterApplyFilterCallBack={pageData.calculateMaxNumberOfMilestone}
      />
    );

    menus.push(
      <Button key="PreconstructionLogPage_importLog" disabled>
        Import Log
      </Button>
    );

    menus.push(
      <Button
        key="PreconstructionLogPage_export"
        placeholder="Export Bid Package Log"
        onClick={() => {
          if (projectDetails && gridRef.current)
            exportEntityLogAsCSV({
              entityName: "Bid Package Log",
              projectDetails,
              gridRef: {
                api: gridRef.current!.api
              },
              includedColumns: ["description"],
              excludedColumns: ["_milestones", "_durations"]
            });
        }}
        disabled={!pageData.bidPackageList?.bid_package_list_func}
        data-testid="index-button-export"
      >
        Export
      </Button>
    );

    menus.push(
      <Button
        key="PreconstructionLogPage_new"
        disabled={cannotWriteAccessOfBidPackage}
        title={
          cannotWriteAccessOfBidPackage
            ? ErrorMessages.PermissionNotGranted
            : "Create New Bid Package"
        }
        onClick={() => {
          setShowDrawer(!showDrawer);
        }}
        type="primary"
        data-testid="index-button-new-bidpackage"
      >
        New Bid Package
      </Button>
    );

    return menus;
  };

  const onEditCell = useCallback(
    (params: any) => {
      gridRef?.current?.api?.setFocusedCell(params.rowIndex, params.colKey);
      gridRef?.current?.api?.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.colKey
      });
    },
    [gridRef]
  );

  const entityName = (
    <div className="flex items-center">
      <CiqIconBox feature="BID_PACKAGE" size={20} padding={8} />
      <div className="flex flex-col pl-3">
        <div className="flex items-center space-x-2">
          <div className="text-black text-xl font-semibold">Bid Packages</div>
          <DemoTextIcon />
        </div>
        <div className="text-xs font-normal">Preconstruction</div>
      </div>
    </div>
  );

  return (
    <>
      {contextHolder}
      <CiqGridViewWrapper
        headerInfo={{
          entityName,
          titleParam: {
            title: "Bid Packages",
            totalCount: pageData.bidPackageList?.bid_package_list_func?.length,
            filterStats: statsData
          },
          items: headerItems(),
          onAllRecordsClick: onAllBidPackagesClick
        }}
        gridInfo={{
          gridRef,
          columnDefs: agGridData.columnDefs,
          rowData: pageData.bidPackageList?.bid_package_list_func,
          defaultColDef: agGridData.defaultColDef,
          rowHeight: 62,
          getRowId, // After refetch the rowdata ,It help to persist Scroll Bar
          readOnlyEdit: true,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
          onCellEditRequest,
          onFirstDataRendered,
          onSortChanged: () => {
            if (isFirstDataRendered && !bidPackageListFilter)
              pageData.onSaveColumnState();
          },
          onFilterChanged: (event: any) => {
            filterChipRef.current?.onFilterApplied();
            setStatsData(
              calculateLogPageStats({
                gridRef,
                ListFilter: bidPackageListFilter,
                featureTypeId: FeatureTypes.BID_PACKAGE
              })
            );
            updateGridSelectionOnFilter(event, gridRef);
            if (isFirstDataRendered && !bidPackageListFilter)
              pageData.onSaveColumnState();
          },
          onColumnVisible: (e) => {
            if (e.api && e.source === "toolPanelUi") {
              if (e.column) {
                agGridData.milestonesColumns.onColumnVisible(
                  e,
                  pageData.maxNumberOfMilestone
                );
              } else {
                agGridData.milestonesColumns.updateAllMileStoneInitialVisibility(
                  e.api,
                  pageData.maxNumberOfMilestone
                );
              }
              pageData.onSaveColumnState();
            }
          },
          onDragStopped: (e) => {
            const classNames = [
              "ag-header-cell-resize",
              "ag-tool-panel-horizontal-resize"
            ];
            if (e.api && !classNames.includes(e.target.className)) {
              pageData.onSaveColumnState();
            }
          },
          context: {
            onEditCell,
            isGridCellEditable: agGridData.isGridCellEditable,
            refetchLogData: pageData.refetchBidPackageList,
            editPermission: !cannotWriteAccessOfBidPackage,
            featureHeaderMap: pageData.bidPackageHeaderMap
          },
          onSelectionChanged: () => {
            setSelectedRows(gridRef?.current?.api?.getSelectedRows() || null);
          },
          loading: !isFirstDataRendered,
          onColumnResized: (params: any) => {
            if (params.finished) {
              // User has finished resizing, do something like save column state
              pageData.onSaveColumnState();
            }
          },
          disableResizingAutoWidth: true
        }}
      />
      {showDrawer && (
        <CreateBidPackageComponent
          setDrawerOpen={setShowDrawer}
          showDrawerOpen={showDrawer}
          modelTitle="Create Bid Package"
          onBidPackageCreated={(title: string, id: string) => {
            setShowDrawer(false);
            openNotification(id, title);
            pageData.refetchBidPackageList();
          }}
        />
      )}

      <Modal
        title="Bulk Edit Details"
        className="custom-drawer"
        width={420}
        style={SidepanelStyles.style}
        footer={null}
        open={bulkEditMode === "details"}
        data-testid="index-modal"
        onCancel={() => {
          setBulkEditMode(null);
        }}
        destroyOnClose
      >
        <ErrorBoundary>
          <BulkEditBidPackageDetails
            selectedRows={selectedRows!}
            onCancel={() => {
              setBulkEditMode(null);
            }}
            onUpdateSuccess={() => {
              setBulkEditMode(null);
              setSelectedRows(null);
              gridRef.current?.api?.deselectAll();
              setTimeout(() => {
                pageData.refetchBidPackageList();
              });
            }}
          />
        </ErrorBoundary>
      </Modal>

      {selectedRows && bulkEditMode === "deadline_date" && (
        <BulkEditDeadlineDateModal
          onUpdateDone={() => {
            setBulkEditMode(null);
            gridRef?.current?.api?.deselectAll();
            pageData.refetchBidPackageList();
          }}
          setShowEdit={setBulkEditMode}
          showEdit={bulkEditMode === "deadline_date"}
          selectedRows={selectedRows}
          columnHeaderMap={pageData.bidPackageHeaderMap}
          featureTypeId={FeatureTypes.BID_PACKAGE}
        />
      )}

      <Modal
        title="Bulk Edit Durations"
        className="custom-drawer with-padding-fix"
        style={SidepanelStyles.style}
        width="calc(100vw - 200px)"
        footer={[
          <div className="flex w-full space-x-3 justify-end" key={100}>
            <Button
              onClick={() => {
                setBulkEditMode(null);
              }}
              key={101}
              data-testid="index-button-close"
            >
              Close
            </Button>
          </div>
        ]}
        open={bulkEditMode === "durations"}
        onCancel={() => {
          setBulkEditMode(null);
        }}
        destroyOnClose
        keyboard={false}
      >
        <ErrorBoundary>
          <FeatureBulkEditDurations
            selectedRows={selectedRows!}
            featureTypeId={FeatureTypes.BID_PACKAGE}
            onUpdateDone={() => {
              setTimeout(() => {
                pageData.refetchBidPackageList();
              });
            }}
          />
        </ErrorBoundary>
      </Modal>
    </>
  );
}

export default PreconstructionLogPage;
